import { useState, useRef, useCallback, useMemo } from "react";
import { useLocalizedStrings } from "../Localization";
import { useLocation } from "react-router-dom";
import { Button, Icon, useToast } from "@aidkitorg/component-library";
import { copyToClipboard } from "../Util";

const SCALE_FACTOR = 1.25;

export function TurnableImage(props: { Viewer: string, src: string; alt: string, fullScreenUrl: string, fullScreen?: boolean }) {
  const L = useLocalizedStrings();
  const [rotation, setRotation] = useState(0);
  const [scale, setScale] = useState(1);
  const [imageError, isImageError] = useState(false);
  const ref = useRef<HTMLImageElement | null>(null);
  const toast = useToast();

  const modernUI = window.location.pathname.includes("/a/");

  const createHandler = useCallback((handler: () => void) => (e: React.MouseEvent) => {
    handler();
    e.preventDefault();
    e.stopPropagation();
    return false;
  }, []);

  const rotate = useMemo(() => createHandler(() => 
    setRotation((rotation) => (rotation + 90) % 360),
  ), []);

  const zoomIn = useMemo(() => createHandler(() => 
    setScale((scale) => scale * SCALE_FACTOR),
  ), []);

  const zoomOut = useMemo(() => createHandler(() => 
    setScale((scale) => scale / SCALE_FACTOR),
  ), []);

  const copyLink = useMemo(() => createHandler(() => {
    copyToClipboard(props.fullScreenUrl);
    toast.toast({ variant: 'success', title: L.questions.attachment.copied_link });
  }), []);

  if (imageError) {
    return (
      <li key={props.src}>
        <a href={props.src}
          onClick={async (e) => {
            e.preventDefault();
            window.open(props.src, "_blank");
          }}
          rel="noopener noreferrer" target="_blank">
          View Document
        </a>
      </li>
    );
  }

  return (
    <div className={"px-2 py-2" + (props.fullScreen ? " h-0" : "")}>
      {props.Viewer === 'screener' &&
        <div className="flex gap-4">
          <Button
            variant="secondary"
            onClick={rotate}
            icon={props.fullScreen ? <Icon name="ArrowPathIcon" /> : undefined}
            className="mb-2"
          >
            {L.questions.attachment.rotate_image}
          </Button>
          <Button
            variant="secondary"
            onClick={zoomIn}
            icon={props.fullScreen ? <Icon name="ArrowsPointingOutIcon" /> : undefined}
            className="mb-2"
          >{L.questions.attachment.zoom_in}
          </Button>
          <Button
            variant="secondary"
            onClick={zoomOut}
            icon={props.fullScreen ? <Icon name="ArrowsPointingInIcon" /> : undefined}
            className="mb-2"
          >{L.questions.attachment.zoom_out}
          </Button>
          <Button
            variant="secondary"
            onClick={copyLink}
            icon={props.fullScreen ? <Icon name="LinkIcon" /> : undefined}
            className="mb-2"
          >{L.questions.attachment.copy_link}
          </Button>
        </div>
      }
      <div style={{
        // We want to scale out from the top so that the top of the image doesn't move,
        // but we want to rotate the image from the center. Thus we scale the container of the image,
        // not the image itself, so it can have a different transform origin.
        transformOrigin: 'top left',
        transform: 'scale(' + scale + ')',
        ...(rotation === 90 || rotation === 270) ? { height: ref?.current?.width ? ref.current.width : 'auto' } : {}
      }}>
        <img
          ref={ref}
          alt={props.alt}
          style={{
            maxWidth: (props.fullScreen || modernUI) ? "100%" : props.Viewer === 'screener' ? "400px" : "300px",
            transform: 'translateY(' + (!props.fullScreen && (rotation === 90 || rotation === 270) ? (((ref.current?.width || 0) - (ref.current?.height || 0)) / 2) : 0) + 'px) ' +
                            'translateX(' + (!props.fullScreen && (rotation === 90 || rotation === 270) ? (((ref.current?.height || 0) - (ref.current?.width || 0)) / 2) : 0) + 'px) ' +
                            'rotate(' + rotation + 'deg)',
            ...(props.fullScreen ? {objectFit: 'contain', maxHeight: '100%', height: '100%', width: '100%'} : {})
          }}
          onError={() => isImageError(true)}
          src={props.src}
        />
      </div>
    </div>
  );
}

export function FullScreenTurnableImagePage() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const src = params.get("src");

  if (!src) {
    return <p>Error: No image source provided.</p>;
  }

  return (
    <div className="w-screen h-screen">
      <TurnableImage Viewer={'screener'} src={src} alt="Full screen turnable image" fullScreen={true} fullScreenUrl={window.location.href} />
    </div>
  );
}
